import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FunLoader, LoadingButton, StudyLesson, StudyPlanFeedback, StudyPlanTopic } from '@lf/ui-primitives';
import EditTopic from '../../components/EditTopic';
import {
    postStudyPlanRevisionEvent,
    reviseTopicsFromFeedback,
    streamStudyPlanTopics,
} from '@lf/api-client/lfApiStudyPlan';

const FEEDBACK_MAX_LEN = 400;
const MIN_NUM_TOPICS = 2;
const MAX_NUM_TOPICS = 5;

const ReviewTopics = ({
    lessonsWithTopics,
    originalGeneratedLessonsWithTopics,
    plan,
    username,
    lessons,
    planProp,
    setPlanProp,
    studyPlanDraftId,
    setStudyPlanDraftId,
    setLessonsWithTopics,
}) => {
    const [draggingDraggableId, setDraggingDraggableId] = useState('');
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingTopic, setEditingTopic] = useState(null);
    const [editingLessonIndex, setEditingLessonIndex] = useState(null);
    const [editingTopicIndex, setEditingTopicIndex] = useState(null);

    const [feedback, setFeedback] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);

    const isTopicSelected = (lessonIndex, topicIndex) => {
        return selectedTopic
            && selectedTopic.lessonIndex === lessonIndex
            && selectedTopic.topicIndex === topicIndex;
    };

    const emitRevisionEvent = async (eventType, beforeLessons, afterLessons, targets = []) => {
        try {
            const data = await postStudyPlanRevisionEvent({
                child_username: username,
                study_plan_draft_id: studyPlanDraftId || undefined,
                subject: plan.subject,
                goal: plan.goal,
                previous_knowledge: plan.previous_knowledge,
                needs: plan.needs,
                step: 'topics',
                event_source: 'user',
                event_type: eventType,
                target_lesson_ids: targets,
                before_lessons: beforeLessons,
                after_lessons: afterLessons,
            });
            if (data?.study_plan_draft_id) {
                setStudyPlanDraftId(data.study_plan_draft_id);
            }
        }
        catch (error) {
            console.error('Error recording topic revision event:', error);
        }
    };

    const handleTopicSelect = (lessonIndex, topicIndex) => {
        if (isReorderMode) return;

        if (isTopicSelected(lessonIndex, topicIndex)) {
            setSelectedTopic(null);
        } else {
            setSelectedTopic({ lessonIndex, topicIndex });
        }
    };

    const handleEditTopic = (lessonIndex, topicIndex) => {
        const topic = lessonsWithTopics[lessonIndex].topics[topicIndex];
        setEditingTopic(topic);
        setEditingLessonIndex(lessonIndex);
        setEditingTopicIndex(topicIndex);
        setIsEditDialogOpen(true);
    };

    const handleSaveEdit = (updatedTopic) => {
        const before = [...lessonsWithTopics];
        const updatedLessons = [...lessonsWithTopics];
        updatedLessons[editingLessonIndex].topics[editingTopicIndex] = updatedTopic;

        setLessonsWithTopics(updatedLessons);
        setIsEditDialogOpen(false);
        setEditingTopic(null);
        setEditingLessonIndex(null);
        setEditingTopicIndex(null);
        void emitRevisionEvent('manual_edit', before, updatedLessons, [updatedLessons[editingLessonIndex].id]);
    };

    const handleDeleteTopic = (lessonIndex, topicIndex) => {
        const before = [...lessonsWithTopics];
        const updatedLessons = [...lessonsWithTopics];
        updatedLessons[lessonIndex].topics.splice(topicIndex, 1);

        setLessonsWithTopics(updatedLessons);
        setSelectedTopic(null);
        void emitRevisionEvent('manual_delete', before, updatedLessons, [updatedLessons[lessonIndex].id]);
    };

    const onDragTopic = (result) => {
        const { source, destination } = result;
        setDraggingDraggableId('');

        const sourceLessonIndex = parseInt(source.droppableId.split(':')[1], 10);
        const before = [...lessonsWithTopics];
        const updatedLessons = [...lessonsWithTopics];

        if (!destination) {
            const sourceLesson = updatedLessons[sourceLessonIndex];
            sourceLesson.topics.splice(source.index, 1);
            setLessonsWithTopics(updatedLessons);
            setSelectedTopic(null);
            void emitRevisionEvent('manual_delete', before, updatedLessons, [
                updatedLessons[sourceLessonIndex].id,
            ]);
            return;
        }

        const destinationLessonIndex = parseInt(destination.droppableId.split(':')[1], 10);

        const sourceLesson = updatedLessons[sourceLessonIndex];
        const [movedTopic] = sourceLesson.topics.splice(source.index, 1);
        const destinationLesson = updatedLessons[destinationLessonIndex];
        destinationLesson.topics.splice(destination.index, 0, movedTopic);

        setLessonsWithTopics(updatedLessons);
        void emitRevisionEvent('manual_reorder', before, updatedLessons, [
            updatedLessons[sourceLessonIndex].id,
            updatedLessons[destinationLessonIndex].id,
        ]);
    };

    const onDragUpdate = (update) => {
        setDraggingDraggableId(!update.destination ? update.draggableId : '');
    };

    const incrementNumTopics = (incr) => {
        const currentNumTopics = parseInt(planProp.num_topics, 10);
        const newNumTopics = currentNumTopics + incr;
        if (newNumTopics < MIN_NUM_TOPICS || newNumTopics > MAX_NUM_TOPICS) {
            return null;
        }
        const newState = { ...planProp, num_topics: newNumTopics };
        setPlanProp(newState);
        return newState;
    };

    const handleMakeLonger = async () => {
        const updatedPlanProp = incrementNumTopics(1);
        if (!updatedPlanProp) return;
        const generated = await streamStudyPlanTopics(
            plan, username, lessons, updatedPlanProp, 'gemini', setLessonsWithTopics,
        );
        setLessonsWithTopics(generated);
    };

    const handleMakeShorter = async () => {
        const updatedPlanProp = incrementNumTopics(-1);
        if (!updatedPlanProp) return;
        const generated = await streamStudyPlanTopics(
            plan, username, lessons, updatedPlanProp, 'gemini', setLessonsWithTopics,
        );
        setLessonsWithTopics(generated);
    };

    const handleRegenerateFromFeedback = async () => {
        if (!feedback.trim()) return;

        setIsRegenerating(true);
        try {
            const response = await reviseTopicsFromFeedback({
                child_username: username,
                study_plan_draft_id: studyPlanDraftId || undefined,
                subject: plan.subject,
                goal: plan.goal,
                previous_knowledge: plan.previous_knowledge,
                needs: plan.needs,
                feedback: feedback.trim(),
                original_lessons: originalGeneratedLessonsWithTopics,
                working_lessons: lessonsWithTopics,
                target_lesson_ids: [],
                llm_name: 'gemini',
            });
            setLessonsWithTopics(response.lessons || []);
            if (response.study_plan_draft_id) {
                setStudyPlanDraftId(response.study_plan_draft_id);
            }
        }
        catch (error) {
            console.error('Error regenerating topics from feedback:', error);
        }
        finally {
            setIsRegenerating(false);
        }
    };

    return (
        <>
            <EditTopic
                topic={editingTopic}
                isOpen={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                onSave={handleSaveEdit}
            />

            <div className="text-left mt-4 sm:flex sm:items-start sm:gap-4">
                <div className="sm:flex-1 sm:min-w-0">
                    <DragDropContext onDragEnd={onDragTopic} onDragUpdate={onDragUpdate}>
                        <div className="my-2 flex items-center">
                            <div className="font-semibold">Suggested topics</div>
                            <div className="flex-grow" />
                            <LoadingButton
                                imgSrc="/icon-list-plus2.png"
                                text="Make longer"
                                onClick={handleMakeLonger}
                            />
                            <LoadingButton
                                imgSrc="/icon-list-minus2.png"
                                text="Make shorter"
                                onClick={handleMakeShorter}
                            />
                            <LoadingButton
                                imgSrc="/icon-list-reorder3.png"
                                text={isReorderMode ? 'Done Reordering' : 'Reorder Topics'}
                                bgColor={isReorderMode ? 'white' : 'transparent'}
                                onClick={() => {
                                    setIsReorderMode(!isReorderMode);
                                    setSelectedTopic(null);
                                }}
                            />
                        </div>

                        {lessonsWithTopics.length === 0 && (
                            <FunLoader
                                title="Building your study plan..."
                                subtitle="Generating topics for each lesson"
                                ariaLabel="Generating study plan topics"
                            />
                        )}
                        {lessonsWithTopics.map((lesson, lessonIndex) => (
                            <Droppable key={`topics:${lessonIndex}`} droppableId={`topics:${lessonIndex}`}>
                                {(provided) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps} className="text-gray-500">
                                        <StudyLesson
                                            lesson={lesson}
                                            index={lessonIndex}
                                            isLastLesson={lessonIndex === lessonsWithTopics.length - 1}
                                        >
                                            {lesson.topics && lesson.topics.map((topic, topicIndex) => (
                                                <div key={`topic:${topic.id}`} className="text-black relative">
                                                    {isReorderMode ? (
                                                        <Draggable
                                                            draggableId={`topics:${lessonIndex}:${topicIndex}`}
                                                            key={`topics:${lessonIndex}:${topicIndex}`}
                                                            index={topicIndex}
                                                        >
                                                            {(dragProvided) => (
                                                                <div
                                                                    ref={dragProvided.innerRef}
                                                                    {...dragProvided.draggableProps}
                                                                    className={`${`topics:${lessonIndex}:${topicIndex}` === draggingDraggableId ? 'opacity-50' : 'opacity-100'}`}
                                                                >
                                                                    <div className="pr-10">
                                                                        <StudyPlanTopic topic={topic} index={topicIndex} topicType="current" />
                                                                    </div>
                                                                    <div
                                                                        {...dragProvided.dragHandleProps}
                                                                        className="absolute top-0 bottom-0 right-2 flex items-center justify-center h-full cursor-grab"
                                                                    >
                                                                        <span className="text-2xl text-gray-500 px-2">≡</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ) : (
                                                        <div
                                                            className={`cursor-pointer ${isTopicSelected(lessonIndex, topicIndex) ? 'pr-20' : ''}`}
                                                            onClick={() => handleTopicSelect(lessonIndex, topicIndex)}
                                                        >
                                                            <StudyPlanTopic
                                                                topic={topic}
                                                                index={topicIndex}
                                                                topicType={isTopicSelected(lessonIndex, topicIndex) ? '' : 'current'}
                                                            />

                                                            {isTopicSelected(lessonIndex, topicIndex) && (
                                                                <div className="absolute top-0 right-2 flex space-x-1 z-10">
                                                                    <LoadingButton
                                                                        imgSrc="/icon-edit.png"
                                                                        text="Edit"
                                                                        bgColor="white"
                                                                        onClick={() => handleEditTopic(lessonIndex, topicIndex)}
                                                                    />
                                                                    <LoadingButton
                                                                        imgSrc="/icon-trash.png"
                                                                        text="Delete"
                                                                        bgColor="white"
                                                                        onClick={() => handleDeleteTopic(lessonIndex, topicIndex)}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {provided.placeholder}
                                        </StudyLesson>
                                    </div>
                                )}
                            </Droppable>
                        ))}
                    </DragDropContext>
                </div>

                {lessonsWithTopics.length > 0 && (
                    <StudyPlanFeedback
                        contentType="topics"
                        feedback={feedback}
                        maxLength={FEEDBACK_MAX_LEN}
                        placeholder="Add more beginner-friendly topics and remove repeated ideas."
                        isRegenerating={isRegenerating}
                        onFeedbackChange={setFeedback}
                        onSubmit={handleRegenerateFromFeedback}
                        className="sm:mt-10 sm:w-64 md:w-96 sm:flex-shrink-0"
                    />
                )}
            </div>
        </>
    );
};

export default ReviewTopics;
